import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { faBooks } from '@fortawesome/pro-solid-svg-icons'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'

import { ActiveFilterViewer, RankmiTable } from '@rankmi/rankmi-table'
import { sortTableEnum } from 'enum/sortTable.enum'
import { QueryUsersArgs, TDataUsers, USERS } from 'graphqlAPI'
import { IAreaModel } from 'models/area.model'
import { applyFilters } from 'helpers/filterData.helper'
import {
  generateTableLocale,
  joinKeys,
  useTranslationRankmi,
} from 'helpers/translations.helper'
import { ChildFilterTypes } from 'enum'
import { canSkipOnRowSelected } from 'helpers'
import { usePaginationVariables } from 'hooks/usePaginationVariables'
import { SortValue } from 'models/rankmiTable.model'
import { ApplyFiltersByType } from '../../types'
import { useColumns } from './hooks/useColumns'
import { prefixTable } from './hooks/useColumns/constants'
import { useFiltersColumns } from './hooks/useFiltersColumns'
import { Buttons } from './Buttons'

import styles from './Participants.module.scss'

interface ParticipantsProps {
  area?: IAreaModel | null
  applyFiltersByType: ApplyFiltersByType
}

export const Participants = ({
  area,
  applyFiltersByType,
}: ParticipantsProps) => {
  const { text, perPage, page, sorts, setPerPage, setPage, setSorts, setText } =
    usePaginationVariables()

  const [showFiltersModal, setShowFiltersModal] = useState(false)

  const [selectedFilters, setSelectedFilters] = useState<IFilterData>()

  const [selected, setSelected] = useState<number[]>([])
  const [allSelected, setAllSelected] = useState(false)

  const areaId = area?.id?.toString()

  const gParent = useMemo(() => {
    const { filters } = applyFiltersByType(ChildFilterTypes.Participant)
    return filters
  }, [applyFiltersByType])

  useEffect(() => {
    setPage(1)
  }, [setPage, gParent])

  type Args = QueryUsersArgs

  const gUsers = useMemo(() => applyFilters(selectedFilters), [selectedFilters])

  const g = useMemo(() => [...gUsers, ...gParent], [gUsers, gParent])

  const variables = useMemo(
    (): QueryUsersArgs => ({
      areaId,
      perPage,
      page,
      q:
        sorts.length || text.length || g?.length
          ? {
              sorts: sorts,
              search: text,
              g,
            }
          : undefined,
    }),
    [areaId, perPage, page, sorts, text, g]
  )

  const { data: dataParticipants, loading: loadingParticipants } = useQuery<
    TDataUsers,
    Args
  >(USERS, {
    variables,
    fetchPolicy: 'cache-and-network',
  })

  const loading = loadingParticipants

  const { users } = { ...dataParticipants }
  const total = users?.nodesCount || 0

  const columns = useColumns()
  const filtersColumns = useFiltersColumns()

  const dataSource = useMemo(
    () =>
      users?.nodes.map((user) => ({
        ...user,
        key: user.id,
      })) || [],
    [users]
  )

  const tRelative = useTranslationRankmi({
    prefix: joinKeys(prefixTable),
  })

  const locale = useMemo(
    () =>
      generateTableLocale({
        header: {
          rowsCount: tRelative('rowsCount'),
          rowsCount_plural: tRelative('rowsCount_plural'),
        },
      }),
    [tRelative]
  )

  return (
    <Row className={classNames('mt-4', styles.root)}>
      <Col xs={12}>
        <Buttons
          allSelected={allSelected}
          filtersColumns={filtersColumns}
          selectedFilters={selectedFilters}
          onChangeFilters={(filters: any) => {
            setSelectedFilters(filters)
          }}
          selected={selected}
          total={total}
          area={area}
          g={g}
          sorts={sorts}
          search={text}
          showFiltersModal={showFiltersModal}
          setShowFiltersModal={setShowFiltersModal}
        />
      </Col>
      {selectedFilters && (
        <Col xs={12} className="mt-4">
          <ActiveFilterViewer
            activeFilters={selectedFilters}
            onChange={(data: any) => setSelectedFilters(data)}
            openFilters={(state: boolean) => setShowFiltersModal(state)}
            columns={filtersColumns}
          />
        </Col>
      )}
      <Col
        xs={12}
        className={styles.tableContainer}
        style={
          {
            '--monitor-participants-name-column-width': '260px',
            '--monitor-participants-action-column-width': '45px',
          } as React.CSSProperties
        }
      >
        <RankmiTable
          columnHeaders={columns}
          data={dataSource}
          selectedRows={selected}
          total={total}
          currentPage={page}
          changeColumnsVisibility
          onPaginationChange={(page: any) => setPage(page)}
          onSearch={(text: string) => setText(text)}
          search={text}
          onSortChange={({ field, order }: SortValue) =>
            setSorts(order ? [`${field} ${sortTableEnum[order]}`] : [])
          }
          loading={loading}
          onRowsPerPageChange={setPerPage}
          onRowSelected={({
            allSelected,
            unselectedRowKeys,
            selectedRowKeys,
            dispatcher,
          }: any) => {
            if (canSkipOnRowSelected(dispatcher)) return

            setSelected(allSelected ? unselectedRowKeys : selectedRowKeys)
            setAllSelected(allSelected)
          }}
          countIcon={faBooks}
          initialRowsPerPage={10}
          simpleSelectAll
          responsive
          locale={locale}
        />
      </Col>
    </Row>
  )
}
